import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useRouteMatch, useLocation, useHistory, Link } from 'react-router-dom';

import AdBrowser from './AdBrowser';
import KeywordManager from './KeywordManager';
import PromptEditor from './PromptEditor';
import ProductMapping from './ProductMapping';
import GeneratedAdsHistory from './GeneratedAdsHistory';
import Creators from './Creators';

// Manual ad upload — formerly the standalone Transcription tab — now lives
// inside Fetched Ads as the "Upload ad" button + UploadAdDialog.
const TAB_ROUTES = [
  { path: 'fetched-ads', label: 'Fetched Ads', component: AdBrowser },
  { path: 'keywords', label: 'Keywords', component: KeywordManager },
  { path: 'prompts', label: 'Prompts', component: PromptEditor },
  { path: 'product-mapping', label: 'Script Builder', component: ProductMapping },
  { path: 'scripts', label: 'Scripts', component: GeneratedAdsHistory },
  { path: 'creators', label: 'Creators', component: Creators },
];

// Tabs are lazy-mounted on first visit and then kept mounted, hidden via
// display:none when inactive. This preserves table data, scroll position,
// expansion state, and selected filters across tab switches — the user no
// longer pays a refetch cost just for navigating between tabs. Components
// own their own "Refresh" buttons for explicit re-fetches.
export default function AdLibraryPage() {
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const activeIndex = TAB_ROUTES.findIndex(t => location.pathname.startsWith(`${url}/${t.path}`));
  const active = activeIndex === -1 ? 0 : activeIndex;

  // Redirect bare /ad-library → /ad-library/fetched-ads exactly once. Done
  // via useEffect instead of a <Redirect> child so the tab DOM stays
  // mounted across navigations.
  useEffect(() => {
    if (activeIndex === -1 && (location.pathname === url || location.pathname === `${url}/`)) {
      history.replace(`${url}/${TAB_ROUTES[0].path}`);
    }
  }, [activeIndex, location.pathname, url, history]);

  const [mounted, setMounted] = useState<Set<number>>(() => new Set([active]));
  useEffect(() => {
    setMounted(prev => prev.has(active) ? prev : new Set([...prev, active]));
  }, [active]);

  return (
    <Box sx={{ minWidth: '80vw', width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      <Typography variant="h5" fontWeight={600}>
        Video Creative
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ position: 'absolute', top: 8, right: 8 }}>
        v1.1.0
      </Typography>
      <Tabs value={active}>
        {TAB_ROUTES.map((t) => (
          <Tab key={t.path} label={t.label} component={Link} to={`${url}/${t.path}`} />
        ))}
      </Tabs>
      <Box sx={{ mt: 1 }}>
        {TAB_ROUTES.map((t, i) => {
          if (!mounted.has(i)) return null;
          const Comp = t.component;
          return (
            <Box key={t.path} sx={{ display: i === active ? 'block' : 'none' }}>
              <Comp />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
